import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import MonaSansRegular from "../../assets/fonts/Mona-Sans-Regular.ttf";
import MonaSansBold from "../../assets/fonts/Mona-Sans-Bold.ttf";
import { user } from "../../assets/images/images";

const BREAK_TYPES = [
  "coffe-break",
  "coffee-break",
  "lunch-break",
  "cocktail-break",
  "breakfast",
  "registration",
];

const NETWORKING_TYPES = ["networking", "other_networking"];

const getDayIndex = (value) => {
  const match = String(value || "").match(/\d+/);
  return match ? Number(match[0]) : 999;
};

const toTitleCase = (value = "") =>
  value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const sortSchedules = (items = []) =>
  [...items].sort((a, b) => {
    const dayDiff = getDayIndex(a?.day) - getDayIndex(b?.day);
    if (dayDiff !== 0) return dayDiff;

    const timeA = dayjs(a?.start_time, "hh:mm A");
    const timeB = dayjs(b?.start_time, "hh:mm A");
    return timeA.diff(timeB);
  });

const formatDayDate = (startDate, dayLabel) => {
  if (!startDate) return "";
  const dayOffset = Math.max(getDayIndex(dayLabel) - 1, 0);
  return dayjs(startDate).add(dayOffset, "day").format("Do MMMM, YYYY");
};

const getDescriptionText = (item) => {
  if (item?.title) return item.title;
  if (Array.isArray(item?.description) && item.description.length) {
    return item.description.slice(0, 2).join(" ");
  }
  return toTitleCase(item?.type || "Session");
};

const getPersonName = (person) =>
  [person?.first_name, person?.last_name].filter(Boolean).join(" ") || "TBA";

const getPosition = (person) =>
  person?.metadata?.delegate_details?.position ||
  person?.metadata?.atendee_details?.position ||
  "";

const getCompany = (person) =>
  person?.metadata?.delegate_details?.company ||
  person?.metadata?.atendee_details?.company ||
  "";

const getSpeakerLabel = (speaker) => {
  if (!speaker) return "";

  const name = getPersonName(speaker);
  const company = getCompany(speaker);

  return [name, company].filter(Boolean).join(" | ");
};

const getBreakPalette = (type) => {
  if (type === "lunch-break") {
    return {
      background: "#d8e7c6",
      text: "#4ca144",
      pill: "#ffffff",
      pillText: "#5d8b53",
    };
  }

  return {
    background: "#d7ebf4",
    text: "#3c6e9d",
    pill: "#ffffff",
    pillText: "#3c6e9d",
  };
};

const buildContentBlocks = (items = []) => {
  const blocks = [];
  let sessionBuffer = [];

  const flushSessions = () => {
    if (!sessionBuffer.length) return;
    blocks.push({ type: "session-grid", items: sessionBuffer });
    sessionBuffer = [];
  };

  items.forEach((item) => {
    if (!item?.type) return;

    if (NETWORKING_TYPES.includes(item.type)) {
      flushSessions();
      blocks.push({ type: "networking", item });
      return;
    }

    if (BREAK_TYPES.includes(item.type)) {
      flushSessions();
      blocks.push({ type: "break", item });
      return;
    }

    if (item.type === "panel-discussion") {
      flushSessions();
      blocks.push({ type: "panel", item });
      return;
    }

    sessionBuffer.push(item);
  });

  flushSessions();
  return blocks;
};

const getMeetingTableValue = (meeting, index, isOtherNetworking) => {
  if (isOtherNetworking) {
    return String(index + 1).padStart(2, "0");
  }

  const details =
    meeting?.sender?.user?.metadata?.atendee_details ||
    meeting?.sender?.user?.metadata?.delegate_details ||
    {};
  const type = details?.table_number_type;
  const exhibitionName =
    details?.exhibiton_table_number || details?.exhibition_table_number;
  const meetingTable = details?.table_number;

  const displayValue =
    type === "exhibition"
      ? exhibitionName
      : type === "meeting"
      ? meetingTable
      : meetingTable || exhibitionName;

  return displayValue || String(index + 1).padStart(2, "0");
};

const PersonalAgendaPDF = ({
  agenda,
  userInfo,
  day,
  schedules,
  meetings,
  currentUserId,
  eventDate,
  eventName,
}) => {
  const sourceSchedules = agenda?.length ? agenda : schedules || [];

  if (!sourceSchedules.length) return null;

  const sortedSchedules = sortSchedules(sourceSchedules);
  const daysInAgenda = [...new Set(sortedSchedules.map((item) => item?.day).filter(Boolean))];
  const daysToRender = day && day !== "All" ? [day] : daysInAgenda;

  const getMeetingCards = (networkingItem) => {
    const slots = networkingItem?.slots || [];
    const isOtherNetworking = networkingItem?.type === "other_networking";

    return slots.flatMap((slot, slotIndex) => {
      const relatedMeetings = (meetings || []).filter(
        (meeting) => meeting?.slot?._id === slot?._id
      );

      const visibleMeetings = currentUserId
        ? relatedMeetings.filter(
            (meeting) =>
              meeting?.sender?.user?._id === currentUserId ||
              meeting?.receiver?.user?._id === currentUserId
          )
        : relatedMeetings;

      return visibleMeetings.map((meeting, meetingIndex) => {
        const isCurrentUserSender =
          meeting?.sender?.user?._id === currentUserId;
        const otherParty = currentUserId
          ? isCurrentUserSender
            ? meeting?.receiver?.user
            : meeting?.sender?.user
          : meeting?.receiver?.user || meeting?.sender?.user;

        return {
          id: `${slot?._id || slotIndex}-${meeting?._id || meetingIndex}`,
          title: slot?.title || `Meeting ${String(slotIndex + 1).padStart(2, "0")}`,
          time: `${meeting?.slot?.startTime || slot?.startTime || ""} - ${
            meeting?.slot?.endTime || slot?.endTime || ""
          }`,
          tableNo: getMeetingTableValue(meeting, slotIndex + meetingIndex, isOtherNetworking),
          attendeeName: getPersonName(otherParty),
          attendeePosition: getPosition(otherParty),
          attendeeCompany: getCompany(otherParty),
          attendeePicture: otherParty?.picture || user,
        };
      });
    });
  };

  const renderSessionCard = (item, index) => (
    <View key={`${item?._id || item?.title || item?.type}-${index}`} style={styles.sessionCard}>
      <View style={styles.sessionHeader}>
        <View style={styles.sessionHeaderLeft}>
          <View style={styles.smallBadgeDot} />
          <Text style={styles.sessionLabel}>
            {(item?.short_title || toTitleCase(item?.type || "Session")).toUpperCase()}
          </Text>
        </View>
        <Text style={styles.sessionTime}>
          {item?.start_time} - {item?.end_time}
        </Text>
      </View>

      <View style={styles.sessionBody}>
        <Text style={styles.sessionTitle}>{getDescriptionText(item)}</Text>

        {!!getSpeakerLabel(item?.speaker) && (
          <View style={styles.speakerChip}>
            <Text style={styles.speakerChipName}>{getPersonName(item?.speaker)}</Text>
            {!!getPosition(item?.speaker) && (
              <Text style={styles.speakerChipMeta}>{getPosition(item?.speaker)}</Text>
            )}
            {!!getCompany(item?.speaker) && (
              <Text style={styles.speakerChipCompany}>{getCompany(item?.speaker)}</Text>
            )}
          </View>
        )}
      </View>
    </View>
  );

  const renderBreakSection = (item, index) => {
    const palette = getBreakPalette(item?.type);

    return (
      <View
        key={`${item?._id || item?.title || item?.type}-${index}`}
        style={[styles.breakBanner, { backgroundColor: palette.background }]}
        wrap={false}
      >
        <Text style={[styles.breakTitle, { color: palette.text }]}>
          {(item?.title || toTitleCase(item?.type || "Break")).toUpperCase()}
        </Text>
        <View style={[styles.breakTimePill, { backgroundColor: palette.pill }]}>
          <Text style={[styles.breakTimeText, { color: palette.pillText }]}>
            {item?.start_time} - {item?.end_time}
          </Text>
        </View>
      </View>
    );
  };

  const renderNetworkingSection = (item, index) => {
    const meetingCards = getMeetingCards(item);

    if (!meetingCards.length) return null;

    return (
      <View
        key={`${item?._id || item?.title || item?.type}-${index}`}
        style={styles.sectionWrapper}
      >
        <View style={styles.ribbon}>
          <Text style={styles.ribbonText}>
            {(item?.title || "Pre Schedule Meetings").toUpperCase()}
          </Text>
        </View>

        <View style={styles.meetingGrid}>
          {meetingCards.map((meetingCard) => (
            <View key={meetingCard.id} style={styles.meetingCard} wrap={false}>
              <View style={styles.meetingCardTop}>
                <Text style={styles.meetingCardLabel}>{meetingCard.title}</Text>
                <Text style={styles.meetingCardTime}>{meetingCard.time}</Text>
              </View>

              <View style={styles.meetingCardBody}>
                <Text style={styles.tableLabel}>
                  TABLE NO {String(meetingCard.tableNo).toUpperCase()}
                </Text>

                <View style={styles.meetingSpeakerCard}>
                  <Image src={meetingCard.attendeePicture} style={styles.meetingAvatar} />
                  <Text style={styles.meetingAttendeeName}>{meetingCard.attendeeName}</Text>
                  {!!meetingCard.attendeePosition && (
                    <Text style={styles.meetingAttendeeMeta}>
                      {meetingCard.attendeePosition}
                    </Text>
                  )}
                  {!!meetingCard.attendeeCompany && (
                    <Text style={styles.meetingAttendeeCompany}>
                      {meetingCard.attendeeCompany}
                    </Text>
                  )}
                </View>
              </View>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderPanelSection = (item, index) => {
    const participants = [
      item?.moderator
        ? {
            ...item.moderator,
            role: "Moderator",
          }
        : null,
      ...(item?.panellist || []).map((person) => ({
        ...person,
        role: "Panellist",
      })),
    ].filter(Boolean);

    return (
      <View
        key={`${item?._id || item?.title || item?.type}-${index}`}
        style={styles.sectionWrapper}
      >
        <View style={styles.ribbon}>
          <Text style={styles.ribbonText}>PANEL DISCUSSION</Text>
          <Text style={styles.ribbonTime}>
            {item?.start_time} - {item?.end_time}
          </Text>
        </View>

        <Text style={styles.panelTitle}>{item?.title}</Text>

        <View style={styles.participantGrid}>
          {participants.map((participant, participantIndex) => (
            <View
              key={`${participant?._id || participantIndex}-${participant.role}`}
              style={styles.participantCard}
              wrap={false}
            >
              <Text style={styles.participantRole}>{participant.role}</Text>
              <Image src={participant?.picture || user} style={styles.participantAvatar} />
              <Text style={styles.participantName}>{getPersonName(participant)}</Text>
              {!!getPosition(participant) && (
                <Text style={styles.participantMeta}>{getPosition(participant)}</Text>
              )}
              {!!getCompany(participant) && (
                <Text style={styles.participantCompany}>{getCompany(participant)}</Text>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <Document>
      {daysToRender.map((dayLabel) => {
        const daySchedules = sortedSchedules.filter((item) => item?.day === dayLabel);
        const blocks = buildContentBlocks(daySchedules);
        const pageMeta = [formatDayDate(eventDate?.start_date, dayLabel), eventName]
          .filter(Boolean)
          .join(" | ");

        return (
          <Page key={dayLabel} size="A4" style={styles.page} wrap>
            <View style={styles.pageHeader}>
              <View style={styles.pageHeaderDay}>
                <Text style={styles.pageHeaderDayText}>
                  {String(dayLabel || "").toUpperCase()}
                </Text>
              </View>

              <View style={styles.pageHeaderMeta}>
                <Text style={styles.pageHeaderMetaText}>{pageMeta}</Text>
              </View>
            </View>

            {!!userInfo?.name && (
              <Text style={styles.userMeta}>
                {userInfo.name}
                {userInfo?.company ? ` | ${userInfo.company}` : ""}
              </Text>
            )}

            {blocks.map((block, index) => {
              if (block.type === "session-grid") {
                return (
                  <View key={`sessions-${index}`} style={styles.sessionGrid}>
                    {block.items.map((item, sessionIndex) =>
                      renderSessionCard(item, sessionIndex)
                    )}
                  </View>
                );
              }

              if (block.type === "break") {
                return renderBreakSection(block.item, index);
              }

              if (block.type === "networking") {
                return renderNetworkingSection(block.item, index);
              }

              if (block.type === "panel") {
                return renderPanelSection(block.item, index);
              }

              return null;
            })}
          </Page>
        );
      })}
    </Document>
  );
};

export default PersonalAgendaPDF;

Font.register({
  family: "Mona-Sans",
  fonts: [
    { src: MonaSansRegular, fontWeight: "normal" },
    { src: MonaSansBold, fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    paddingBottom: 28,
    backgroundColor: "#f7f7f5",
    fontFamily: "Mona-Sans",
  },
  pageHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#3b6998",
    minHeight: 84,
  },
  pageHeaderDay: {
    flex: 1,
    paddingVertical: 18,
    paddingHorizontal: 22,
    backgroundColor: "#3f6f9d",
  },
  pageHeaderDayText: {
    color: "#ffffff",
    fontSize: 40,
    fontWeight: "bold",
  },
  pageHeaderMeta: {
    width: 285,
    marginRight: 18,
    backgroundColor: "#ffffff",
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 16,
  },
  pageHeaderMetaText: {
    color: "#3f6f9d",
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
  },
  userMeta: {
    marginTop: 12,
    marginHorizontal: 24,
    color: "#4c6c4c",
    fontSize: 10,
  },
  sessionGrid: {
    marginTop: 18,
    marginHorizontal: 24,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  sessionCard: {
    width: "48.5%",
    marginBottom: 16,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #dce5d4",
  },
  sessionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#f3f4ef",
    borderBottom: "1px solid #dce5d4",
  },
  sessionHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    maxWidth: "58%",
  },
  smallBadgeDot: {
    width: 8,
    height: 8,
    borderRadius: 8,
    backgroundColor: "#59a54c",
    marginRight: 8,
  },
  sessionLabel: {
    color: "#59a54c",
    fontSize: 9,
    fontWeight: "bold",
  },
  sessionTime: {
    color: "#59a54c",
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "right",
  },
  sessionBody: {
    minHeight: 128,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 16,
    alignItems: "center",
    justifyContent: "space-between",
  },
  sessionTitle: {
    color: "#6b9c5f",
    fontSize: 11,
    fontWeight: "bold",
    textAlign: "center",
    lineHeight: 1.35,
  },
  speakerChip: {
    marginTop: 12,
    minWidth: 140,
    maxWidth: 190,
    borderRadius: 12,
    backgroundColor: "#cce1b7",
    paddingVertical: 8,
    paddingHorizontal: 10,
    alignItems: "center",
  },
  speakerChipName: {
    color: "#ffffff",
    backgroundColor: "#58a84a",
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 12,
    fontSize: 8,
    fontWeight: "bold",
    marginBottom: 4,
  },
  speakerChipMeta: {
    color: "#3f4c3d",
    fontSize: 8,
    textAlign: "center",
  },
  speakerChipCompany: {
    color: "#5e9a50",
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "center",
  },
  breakBanner: {
    marginTop: 16,
    marginHorizontal: 0,
    paddingVertical: 16,
    paddingHorizontal: 24,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  breakTitle: {
    fontSize: 22,
    fontWeight: "bold",
  },
  breakTimePill: {
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  breakTimeText: {
    fontSize: 10,
    fontWeight: "bold",
  },
  sectionWrapper: {
    marginTop: 18,
    marginHorizontal: 24,
  },
  ribbon: {
    backgroundColor: "#3e6f9f",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ribbonText: {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: "bold",
  },
  ribbonTime: {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: "bold",
  },
  meetingGrid: {
    marginTop: 16,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  meetingCard: {
    width: "23.5%",
    marginBottom: 14,
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#cfe6f1",
  },
  meetingCardTop: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  meetingCardLabel: {
    color: "#ffffff",
    backgroundColor: "#3e6f9f",
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 12,
    fontSize: 7,
    fontWeight: "bold",
  },
  meetingCardTime: {
    color: "#3e6f9f",
    fontSize: 8,
    fontWeight: "bold",
    marginTop: 8,
    textAlign: "center",
  },
  meetingCardBody: {
    borderTop: "1px solid #ffffff",
    paddingVertical: 10,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  tableLabel: {
    color: "#3e6f9f",
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
  },
  meetingSpeakerCard: {
    marginTop: 12,
    alignItems: "center",
  },
  meetingAvatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    marginBottom: 6,
  },
  meetingAttendeeName: {
    color: "#ffffff",
    backgroundColor: "#58a84a",
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 10,
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
  },
  meetingAttendeeMeta: {
    marginTop: 4,
    color: "#39473b",
    fontSize: 7,
    textAlign: "center",
  },
  meetingAttendeeCompany: {
    color: "#5b9950",
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
  },
  panelTitle: {
    marginTop: 14,
    color: "#3e6f9f",
    fontSize: 17,
    fontWeight: "bold",
    textAlign: "center",
  },
  participantGrid: {
    marginTop: 14,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  participantCard: {
    width: "23.5%",
    marginBottom: 12,
    backgroundColor: "#cfe6f1",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  participantRole: {
    color: "#ffffff",
    backgroundColor: "#3e6f9f",
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 8,
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 8,
  },
  participantAvatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    marginBottom: 6,
  },
  participantName: {
    color: "#3f4c3d",
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "center",
  },
  participantMeta: {
    marginTop: 3,
    color: "#3f4c3d",
    fontSize: 7,
    textAlign: "center",
  },
  participantCompany: {
    color: "#3e6f9f",
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
  },
});








